import { genAttributes } from '@onward-delivery/core';

// The order's destination address, split the way the CSV importer names it: `street` is Address
// Line 1 and `unit` is Address Line 2 (generateOptions.js labels pickup_unit / customer_unit
// exactly that). Kept apart rather than joined so an exported file can be read back in.
//
// genAttributes resolves the side, so a return reads its pickup address the way the table's other
// Destination columns already do. Shared by the table columns and the export columns so the two
// can never disagree.
const destinationPart = (order, key) => {
    const attrs = genAttributes(order);
    return order[attrs[key]] || '-';
};

export const destinationAddress = (order) => destinationPart(order, 'street');
export const destinationAddress2 = (order) => destinationPart(order, 'unit');

// Columns offered in the export picker that have no visible table column behind them. `before`
// names the table column id the export column is slotted ahead of, so the CSV reads in a logical
// order; entries with no surviving anchor append in declaration order.
//
// Destination Address has a table column too, but it is hidden by default -- this is the fallback
// that keeps it exportable without first enabling it. buildExportColumnList drops an entry whose
// header a visible table column already contributes, so only ever one of the two is offered.
export const EXPORT_ONLY_COLUMNS = [
    {
        before: 'destination_city',
        column: { header: 'Destination Address', value: ({ order }) => destinationAddress(order) },
    },
    {
        before: 'destination_city',
        column: { header: 'Destination Address 2', value: ({ order }) => destinationAddress2(order) },
    },
];

export const flattenTableLeafColumns = (cols) =>
    (cols || []).flatMap((c) => (c?.columns?.length ? flattenTableLeafColumns(c.columns) : [c]));

/**
 * Builds the export picker's column list so the CSV mirrors the table the user is looking at:
 * the saved column order first, then any columns saved before those columns existed, with
 * hidden and non-exportable columns dropped. Export-only columns are spliced in ahead of their
 * `before` anchor, or appended when that anchor no longer exists.
 */
export const buildExportColumnList = ({
    leafIds = [],
    savedOrder = [],
    hiddenColumns = [],
    exportersById = {},
    exportOnlyColumns = [],
}) => {
    const available = new Set(leafIds);
    const hidden = new Set(hiddenColumns);

    const seen = new Set();
    const orderedIds = [];
    savedOrder.forEach((id) => {
        if (!seen.has(id) && available.has(id)) {
            orderedIds.push(id);
            seen.add(id);
        }
    });
    leafIds.forEach((id) => {
        if (!seen.has(id)) {
            orderedIds.push(id);
            seen.add(id);
        }
    });

    const isExportable = (id) => !hidden.has(id) && !!exportersById[id];

    // Anchors resolve against the whole column order rather than the visible subset, so an
    // export-only column keeps its slot when the column it sits beside is hidden. Anchoring on
    // visibility made one column's position depend on an unrelated column's.
    const anchors = new Set(orderedIds);

    // ExportCsvTemplateModal keys both `selectedColumns` and its React list by `column.header`, so
    // two entries sharing a header silently merge into one checkbox. The table column wins and the
    // export-only entry stands in only while that column is hidden.
    const visibleHeaders = new Set(orderedIds.filter(isExportable).map((id) => exportersById[id].header));
    const exportOnly = exportOnlyColumns.filter(({ column }) => !visibleHeaders.has(column?.header));

    const columns = orderedIds.flatMap((id) => [
        ...exportOnly.filter(({ before }) => before === id).map(({ column }) => column),
        ...(isExportable(id) ? [exportersById[id]] : []),
    ]);

    const unanchored = exportOnly.filter(({ before }) => !anchors.has(before)).map(({ column }) => column);

    return [...columns, ...unanchored];
};
